import { AreaState } from '../rules/suburb-subregion-edits';
import { HoverInfo } from './map-types';

/**
 * Questions the workspace asks of the map's local copy (#256, #260): what is drawn after the filters, the tallies,
 * the search. Pure functions over the loaded features and the assignments, tested in map-queries.spec.ts.
 */

export interface SearchHit {
  code: string;
  name: string;
  regionId: string | null;
  covered: boolean;
}

export interface RegionTally {
  assigned: number;
  covered: number;
}

/** A search needs this many characters before it lists anything. */
const MIN_SEARCH = 2;
const SEARCH_LIMIT = 40;

const codeOf = (f: any): string => f.properties.code;

/**
 * What's drawn after the view filters. Everything that touches the map (draw, pick, paint, lasso) goes through this,
 * so a hidden suburb can't be clicked, painted or lassoed either.
 */
export function visibleFeatures(
  features: any[],
  areas: ReadonlyMap<string, AreaState>,
  hiddenRegions: ReadonlySet<string>,
  unassignedOnly: boolean
): any[] {
  if (!hiddenRegions.size && !unassignedOnly) return features;
  return features.filter((f) => {
    const region = areas.get(codeOf(f))?.regionId;
    if (unassignedOnly) return !region;
    return !region || !hiddenRegions.has(region);
  });
}

/** Members per sub-region, over every state. */
export function subRegionCounts(areas: ReadonlyMap<string, AreaState>): Map<string, number> {
  const out = new Map<string, number>();
  for (const { subRegions } of areas.values())
    for (const id of subRegions) out.set(id, (out.get(id) ?? 0) + 1);
  return out;
}

/** Assigned and covered counts per region, over every state. */
export function regionCounts(areas: ReadonlyMap<string, AreaState>): Map<string, RegionTally> {
  const out = new Map<string, RegionTally>();
  for (const { regionId, covered } of areas.values()) {
    let tally = out.get(regionId);
    if (!tally) out.set(regionId, (tally = { assigned: 0, covered: 0 }));
    tally.assigned++;
    if (covered) tally.covered++;
  }
  return out;
}

/** How many of the loaded state's suburbs are assigned. */
export function inLoadedState(
  features: any[],
  areas: ReadonlyMap<string, AreaState>
): { assigned: number; total: number } {
  return { assigned: features.filter((f) => areas.has(codeOf(f))).length, total: features.length };
}

/** A drawn suburb's ABS name ('' when it isn't in the loaded state). */
export function areaName(features: any[], code: string): string {
  return String(features.find((f) => codeOf(f) === code)?.properties.name ?? '');
}

/** Suburbs of the loaded state whose name contains the text (case-insensitive), with where they stand. */
export function searchFeatures(
  features: any[],
  areas: ReadonlyMap<string, AreaState>,
  q: string,
  limit = SEARCH_LIMIT
): SearchHit[] {
  const needle = q.trim().toLowerCase();
  if (needle.length < MIN_SEARCH) return [];
  return features
    .filter((f) => String(f.properties.name).toLowerCase().includes(needle))
    .slice(0, limit)
    .map((f) => ({
      code: codeOf(f),
      name: f.properties.name,
      regionId: areas.get(codeOf(f))?.regionId ?? null,
      covered: !!areas.get(codeOf(f))?.covered,
    }));
}

/** The loaded suburbs in a region, or in a sub-region. */
export function featuresIn(
  features: any[],
  areas: ReadonlyMap<string, AreaState>,
  where: { regionId: string } | { subRegionId: string }
): any[] {
  if ('regionId' in where)
    return features.filter((f) => areas.get(codeOf(f))?.regionId === where.regionId);
  return features.filter((f) => areas.get(codeOf(f))?.subRegions.includes(where.subRegionId));
}

/**
 * The hover card's facts for the suburb under the pointer (deck.gl's pick info carries the feature and the pixel).
 * Sub-regions are named; `subRegionName` falls back to the id.
 */
export function hoverInfo(
  info: {
    object: { properties: { code: string; name: string; state: string } };
    x: number;
    y: number;
  },
  areas: ReadonlyMap<string, AreaState>,
  pages: ReadonlyMap<string, { label: string }>,
  subRegionName: (id: string) => string
): HoverInfo {
  const { code, name, state } = info.object.properties;
  const row = areas.get(code);
  return {
    code,
    name,
    state,
    regionId: row?.regionId ?? null,
    covered: !!row?.covered,
    page: pages.get(code)?.label ?? null,
    subRegions: (row?.subRegions ?? []).map(subRegionName),
    x: info.x,
    y: info.y,
  };
}
